import { RequestError } from '@direct-frontend/modules-request';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiCreateSimilarCardsUrl,
    getOpenApiDeleteSimilarCardsUrl,
    getOpenApiGetSimilarCardsUrl,
    getOpenApiUpdateSimilarCardsUrl,
    OpenApiCreateSimilarCardsMethod,
    type OpenApiCreateSimilarCardsRequest,
    type OpenApiCreateSimilarCardsResponseSuccess,
    OpenApiDeleteSimilarCardsMethod,
    OpenApiGetSimilarCardsMethod,
    type OpenApiGetSimilarCardsResponseSuccess,
    OpenApiUpdateSimilarCardsMethod,
    type OpenApiUpdateSimilarCardsRequest,
    type OpenApiUpdateSimilarCardsResponseSuccess,
} from '#shared/api/schema';

type SimilarCardsSettingsQueryResult = {
    settings: OpenApiGetSimilarCardsResponseSuccess | null;
    exists: boolean;
};

const SimilarCardsSettingsTag = 'SimilarCardsSettings' as const;

const similarCardsSettingsApi = createApi({
    name: 'entities/product/api/similarCardsSettings',
    tagTypes: [SimilarCardsSettingsTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getSimilarCardsSettings: builder.query<SimilarCardsSettingsQueryResult, { cardId: string }>({
                queryFn: async ({ cardId }, _api, _extraOptions, baseQuery) => {
                    const result = await baseQuery({
                        url: getOpenApiGetSimilarCardsUrl({ card_id: cardId }),
                        method: OpenApiGetSimilarCardsMethod,
                    });

                    if (result.error) {
                        // 404 — нормальный ответ: ручные настройки similar-cards для карточки ещё не созданы.
                        if (result.error instanceof RequestError && result.error.response?.status === 404) {
                            return { data: { settings: null, exists: false } };
                        }

                        return { error: result.error };
                    }

                    return {
                        data: {
                            settings: (result.data as OpenApiGetSimilarCardsResponseSuccess) ?? null,
                            exists: true,
                        },
                    };
                },
                providesTags: (_result, _error, { cardId }) => [{ type: SimilarCardsSettingsTag, id: cardId }],
            }),
            createSimilarCardsSettings: builder.mutation<
                OpenApiCreateSimilarCardsResponseSuccess,
                { cardId: string; payload: OpenApiCreateSimilarCardsRequest }
            >({
                query: ({ cardId, payload }) => {
                    return {
                        url: getOpenApiCreateSimilarCardsUrl({ card_id: cardId }),
                        method: OpenApiCreateSimilarCardsMethod,
                        body: payload,
                    };
                },
                invalidatesTags: (_result, error, { cardId }) => {
                    if (error) {
                        return [];
                    }

                    return [{ type: SimilarCardsSettingsTag, id: cardId }];
                },
            }),
            updateSimilarCardsSettings: builder.mutation<
                OpenApiUpdateSimilarCardsResponseSuccess,
                { cardId: string; payload: OpenApiUpdateSimilarCardsRequest }
            >({
                query: ({ cardId, payload }) => {
                    return {
                        url: getOpenApiUpdateSimilarCardsUrl({ card_id: cardId }),
                        method: OpenApiUpdateSimilarCardsMethod,
                        body: payload,
                    };
                },
                invalidatesTags: (_result, error, { cardId }) => {
                    if (error) {
                        return [];
                    }

                    return [{ type: SimilarCardsSettingsTag, id: cardId }];
                },
            }),
            deleteSimilarCardsSettings: builder.mutation<void, { cardId: string }>({
                query: ({ cardId }) => {
                    return {
                        url: getOpenApiDeleteSimilarCardsUrl({ card_id: cardId }),
                        method: OpenApiDeleteSimilarCardsMethod,
                    };
                },
                invalidatesTags: (_result, error, { cardId }) => {
                    if (error) {
                        return [];
                    }

                    return [{ type: SimilarCardsSettingsTag, id: cardId }];
                },
            }),
        };
    },
});

export const useSimilarCardsSettingsQuery = similarCardsSettingsApi.useGetSimilarCardsSettingsQuery;
export const createSimilarCardsSettings = similarCardsSettingsApi.endpoints.createSimilarCardsSettings.mutate;
export const updateSimilarCardsSettings = similarCardsSettingsApi.endpoints.updateSimilarCardsSettings.mutate;
export const deleteSimilarCardsSettings = similarCardsSettingsApi.endpoints.deleteSimilarCardsSettings.mutate;
